import {
  useQueries,
  type DefaultError,
  type QueriesResults,
  type QueryKey,
  type UseQueryOptions,
} from '@github-ui/react-query'
import {useAsyncBatchedCallback} from '@tanstack/react-pacer'

/**
 * Batched queries don't define their own `queryFn`; instead they define `variables` to pass to the batched function.
 * All `variables` must be included in the query key.
 */
export interface BatchedUseQueryOptions<
  TQueryFnData = unknown, // even though there's no queryFn, we still need the TQueryFnData param to because it's consumed by `select`
  TVariables = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
> extends Omit<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>, 'queryFn' | 'subscribed'> {
  variables: TVariables
}

/** Context passed to the batched query function. */
export interface BatchedQueryFnContext<TQueryKey extends QueryKey = QueryKey> {
  /** Abort signal for this batch. A batch will only be aborted if every item in the batch aborts. */
  signal: AbortSignal
  /** Array of all query keys in this batch. Order and length will match `variables`. */
  queryKeys: readonly TQueryKey[]
}

/**
 * Result for each query. Matches the shape of `PromiseSettledResult`, allowing the use of `Promise.allSettled` if you
 * already have a promise for each item. Otherwise, this can easily be constructed directly and allows rejecting or
 * fulfilling each individual item.
 */
export type BatchedQueryResult<TData, TError = DefaultError> =
  | {status: 'fulfilled'; value: TData}
  | {status: 'rejected'; reason: TError}

/** Simple helper function to build a rejected value if `data` is `undefined`, otherwise build a fulfilled value. */
export function requiredBatchedQueryResult<TData>(data: TData | undefined): BatchedQueryResult<TData, Error> {
  return data === undefined
    ? {status: 'rejected', reason: new Error('Item not found in batched query data')}
    : {status: 'fulfilled', value: data}
}

/**
 * Batched query function for `useBatchedQueries`.
 *
 * Note on error handling: If the batch promise rejects, every query in the batch will fail. To fail individual queries,
 * resolve the batch promise with `status: 'rejected'` for the failed items.
 *
 * @param variables Array of query `variables` objects for all the items in this batch.
 * @param context See {@link BatchedQueryFnContext}
 *
 * @returns MUST return a result array of the same length and order as the passed `variables` array. If the length
 * differs, every query in the batch will fail. If the order differs, data will be mismatched.
 */
export type BatchedQueryFn<
  TQueryFnData = unknown,
  TVariables = unknown,
  TError = DefaultError,
  TQueryKey extends QueryKey = QueryKey,
> = (
  variables: readonly TVariables[],
  context: BatchedQueryFnContext<TQueryKey>,
) => PromiseLike<ReadonlyArray<BatchedQueryResult<TQueryFnData, TError>>>

type BatchedQueriesResults<TQueryFnData, TError, TData, TQueryKey extends QueryKey> = QueriesResults<
  Array<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>
>

export interface UseBatchedQueriesOptions<
  TQueryFnData = unknown,
  TVariables = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TCombinedResult = BatchedQueriesResults<TQueryFnData, TError, TData, TQueryKey>,
> {
  /**
   * Queries to batch. If provided, the `queryFn` of each query will be ignored in favor of the batched query function.
   * Each query must have a `variables` field which contains the data to pass through to the batched query function.
   *
   * @note Without a query function, it can be difficult for TypeScript to accurately infer the types of `TQueryFnData`
   * and `TVariables`. You may need to explicitly set those.
   * For example: `items.map<BatchedUseQueryOptions<YourData, YourVariables>>(item => ...)`
   */
  queries: ReadonlyArray<BatchedUseQueryOptions<TQueryFnData, TVariables, TError, TData, TQueryKey>>
  /** Batched query function. Accepts an array of query `variables`. Must return a same-sized, same-order array of results. */
  batchedQueryFn: BatchedQueryFn<TQueryFnData, TVariables, TError, TQueryKey>
  /** Optional function to combine queries. Works exactly the same as `useQueries` `combine`. */
  combine?: (result: BatchedQueriesResults<TQueryFnData, TError, TData, TQueryKey>) => TCombinedResult
  subscribed?: boolean
  /**
   * Duration to wait before executing a batch. Note that this is the maximum gap between calls to combine them but it
   * is the **minimum** duration of a batch - the timer resets every time a new item is added. For example, if the
   * `batchDuration` is 1 second and a second call is made at 900 ms, the total batch duration will be 1900 ms. For
   * this reason, use caution when increasing this value.
   * @default 10
   */
  batchDuration?: number
  /**
   * Maximum number of items per batch. This control prevents infinitely-long batches in addition to very large requests;
   * the maximum possible batch duration is `maxBatchSize * batchDuration`, so use caution when increasing this value.
   * @default 20
   */
  maxBatchSize?: number
}

/**
 * `useBatchedQueries` works similarly to `useQueries` but automatically combines several calls together into a single
 * batched call. Calls made within `batchDuration` milliseconds of each other will be combined into batches of up to
 * `maxBatchSize` items, at which point the `batchedQueryFn` will be called. Results will be unpacked back to
 * individual queries.
 *
 * This approach is significantly more efficient than calling `useQueries` or `useQuery` with a batched endpoint,
 * because each individual item retains its own separate entry in the query cache. This allows single items to be updated
 * without re-querying the whole batch, and when one item is reused across several sites, it doesn't need to be fetched
 * several times.
 *
 * @note Batched queries will be delayed to collect the batch, by at least `batchDuration` ms and up to
 * `batchDuration * maxBatchSize` ms. Keep the batch duration and size small to avoid slow experiences.
 *
 * @param options See {@link UseBatchedQueriesOptions}
 * @returns `useQueries` result - either an array of queries, or a combined value if `combine` was provided.
 */
export function useBatchedQueries<
  // Unlike useQueries, useBatchedQueries has the intentional limitation that every query must have the same shape
  // (same data, variables, error). This isn't _strictly_ required by the implementation but trying to support a 1:1
  // mapping of each query to a unique type through the batching process would be very complex.
  TQueryFnData = unknown,
  TVariables = unknown,
  TError = DefaultError,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
  TCombinedResult = BatchedQueriesResults<TQueryFnData, TError, TData, TQueryKey>,
>({
  batchedQueryFn,
  queries,
  batchDuration = 10,
  maxBatchSize = 20,
  ...queriesOptions
}: UseBatchedQueriesOptions<TQueryFnData, TVariables, TError, TData, TQueryKey, TCombinedResult>) {
  interface BatchItem {
    queryKey: TQueryKey
    variables: TVariables
    resolve(data: TQueryFnData): void
    reject(error: TError): void
    signal: AbortSignal
  }

  /**
   * Adds an item to the batch. Note that while this does return a promise, we can't use it to track the state of the
   * batch because it will resolve immediately if the call just starts a timer to collect batched items. So we ignore
   * the returned promise and instead forward resolve/reject through.
   *
   * We don't worry about cancelling or flushing the batch on unmount; we let TSQ handle that through the passed abort
   * signals.
   */
  const addToBatch = useAsyncBatchedCallback(
    async (allItems: readonly BatchItem[]) => {
      // Batches can run after a delay, at which point some items could have already been aborted. Skip those items.
      const items: BatchItem[] = []
      for (const item of allItems)
        if (item.signal.aborted) item.reject(item.signal.reason)
        else items.push(item)

      if (items.length === 0) return

      // Create a new aggregate signal which will abort if every item aborts, cancelling the whole batch
      const aggregateSignal = aggregateAbortSignal(items.map(item => item.signal))

      try {
        const results = await batchedQueryFn(
          items.map(item => item.variables),
          {signal: aggregateSignal.signal, queryKeys: items.map(item => item.queryKey)},
        )

        if (results.length !== items.length)
          throw new Error(
            'Invalid batched query result: Batched result length did not match passed batched items length.',
          )

        for (const [i, result] of results.entries())
          if (result.status === 'fulfilled') items[i]?.resolve(result.value)
          else items[i]?.reject(result.reason)
      } catch (error) {
        for (const item of items) item.reject(error as TError)
        throw error
      } finally {
        aggregateSignal.detach()
      }
    },
    {
      wait: batchDuration,
      maxSize: maxBatchSize,
      throwOnError: false,
      // Flush on unmount, after giving TSQ time to cancel the queries. If it does so, the batch will cancel itself.
      onUnmount: batcher => setTimeout(() => batcher.flush()),
    },
  )

  return useQueries({
    queries: queries.map<UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>>(
      ({variables, queryKey, ...queryOptions}) => ({
        ...queryOptions,
        queryKey,
        queryFn: async ({signal}) =>
          new Promise((resolve, reject) => addToBatch({variables, resolve, reject, signal, queryKey})),
      }),
    ),
    ...queriesOptions,
  })
}

/** Combines several abort signals into a single unified signal that only aborts if **all** the passed signals abort. */
function aggregateAbortSignal(signals: readonly AbortSignal[]) {
  const aggregateController = new AbortController()
  const detachController = new AbortController()

  let remainingSignalsCount = signals.length
  const onAbortItem = () => {
    remainingSignalsCount--
    if (remainingSignalsCount === 0) aggregateController.abort()
  }

  for (const signal of signals)
    if (signal.aborted) onAbortItem()
    else signal.addEventListener('abort', onAbortItem, {once: true, signal: detachController.signal})

  return {
    signal: aggregateController.signal,
    detach: () => detachController.abort(),
  }
}
